import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";

const pick = (items) => items[Math.floor(Math.random() * items.length)];
const configs = [3, 5, 7, 9, 11].flatMap((factor) =>
  [1, 3, 5].map((shift) => ({ factor, shift }))
);

function shuffle(items) {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const target = Math.floor(Math.random() * (index + 1));
    [result[index], result[target]] = [result[target], result[index]];
  }
  return result;
}

function makeChoices(shift) {
  return shuffle([
    {
      value: "one-even",
      label: "Jedna z liczb n oraz n + " + shift + " jest parzysta, więc ich iloczyn jest podzielny przez 2.",
    },
    { value: "both-even", label: "Obie liczby n oraz n + " + shift + " są zawsze parzyste." },
    { value: "both-odd", label: "Obie liczby n oraz n + " + shift + " są zawsze nieparzyste." },
  ]);
}

function normalizeExpression(raw) {
  const source = String(raw || "").trim();
  const equalIndex = source.lastIndexOf("=");
  return (equalIndex >= 0 ? source.slice(equalIndex + 1) : source)
    .replace(/\s/g, "")
    .toLowerCase()
    .replace(/\\left|\\right/g, "")
    .replace(/\\cdot/g, "")
    .replace(/[·×*]/g, "")
    .replace(/\^\{([^}]+)\}/g, "^$1")
    .replace(/\u2212/g, "-");
}

export function isFactorizationCorrect(raw, factor, shift) {
  const compact = normalizeExpression(raw);
  const bracket = "(n+" + shift + ")";
  return [
    factor + "n" + bracket,
    "n" + factor + bracket,
    factor + bracket + "n",
    bracket + factor + "n",
    "n" + bracket + factor,
    bracket + "n" + factor,
  ].includes(compact);
}

export function isDivisorFactorCorrect(raw, factor, shift) {
  if (isFactorizationCorrect(raw, factor, shift)) return true;
  const compact = normalizeExpression(raw);
  const insideA = "(n^2+" + shift + "n)";
  const insideB = "(" + shift + "n+n^2)";
  return [
    factor + insideA,
    insideA + factor,
    factor + insideB,
    insideB + factor,
  ].includes(compact);
}
function buildTask(factor, shift, source) {
  return {
    type: "MaturaParityProductDivisibilityOpenTask",
    data: {
      factor,
      shift,
      linearCoefficient: factor * shift,
      divisor: factor * 2,
      maxPoints: 2,
      choices: makeChoices(shift),
      correctChoice: "one-even",
    },
    source,
    tags: ["matura", "dowod", "podzielnosc", "parzystosc"],
  };
}

export function generateTask(options = {}) {
  if (options.variant === "original") {
    return buildTask(7, 3, {
      variant: "original", exam: "MATURA", session: "2026_MAJ", taskNumber: 7,
    });
  }
  const item = pick(configs);
  return buildTask(item.factor, item.shift, {
    variant: "random", basedOn: "MATURA_2026_MAJ_7",
  });
}

export function renderTask({ data }) {
  const expression = data.factor + "n^2+" + data.linearCoefficient + "n";
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p><strong>Zadanie maturalne – otwarte (0–{data.maxPoints}).</strong></p>
      <p>
        Wykaż, że dla każdej liczby całkowitej <InlineMath math="n" /> liczba{" "}
        <InlineMath math={expression} /> jest podzielna przez{" "}
        <InlineMath math={String(data.divisor)} />.
      </p>
      <p>Uzupełnij dwa kluczowe etapy dowodu.</p>
    </div>
  );
}

function ReasonSelect({ choices, value, onChange, disabled }) {
  const menuRef = React.useRef(null);
  const selected = choices.find((choice) => choice.value === value);
  const label = disabled
    ? "Najpierw poprawnie wykonaj krok 1"
    : selected?.label || "Wybierz uzasadnienie";

  if (disabled) {
    return (
      <div
        role="button"
        aria-disabled="true"
        className="input-secondary"
        style={{
          width: "min(100%, 760px)",
          minHeight: 48,
          padding: "10px 14px",
          display: "flex",
          alignItems: "center",
          opacity: 0.58,
          whiteSpace: "normal",
          overflowWrap: "anywhere",
        }}
      >
        {label}
      </div>
    );
  }

  return (
    <details ref={menuRef} style={{ position: "relative", width: "min(100%, 760px)" }}>
      <summary
        className="input-secondary"
        style={{
          minHeight: 48,
          padding: "10px 42px 10px 14px",
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          listStyle: "none",
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          lineHeight: 1.35,
        }}
      >
        <span style={{ flex: 1 }}>{label}</span>
        <span aria-hidden="true" style={{ position: "absolute", right: 14 }}>⌄</span>
      </summary>
      <div
        role="listbox"
        aria-label="Uzasadnienie podzielności"
        style={{
          position: "absolute",
          zIndex: 20,
          top: "calc(100% + 4px)",
          left: 0,
          width: "100%",
          display: "grid",
          padding: 6,
          border: "1px solid color-mix(in srgb, var(--color-text) 32%, transparent)",
          borderRadius: 8,
          background: "var(--color-surface, #0b0b16)",
          boxShadow: "0 12px 28px rgba(0, 0, 0, 0.38)",
        }}
      >
        {choices.map((choice) => (
          <button
            key={choice.value}
            type="button"
            role="option"
            aria-selected={choice.value === value}
            data-reason-option={choice.value}
            onClick={() => {
              onChange(choice.value);
              menuRef.current?.removeAttribute("open");
            }}
            style={{
              width: "100%",
              padding: "9px 10px",
              border: 0,
              borderRadius: 6,
              background: choice.value === value
                ? "color-mix(in srgb, var(--color-accent) 18%, transparent)"
                : "transparent",
              color: "inherit",
              font: "inherit",
              lineHeight: 1.35,
              textAlign: "left",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              cursor: "pointer",
            }}
          >
            {choice.label}
          </button>
        ))}
      </div>
    </details>
  );
}
export function renderInput(task, userAnswer, onChange, disabled) {
  const value = userAnswer || {};
  const { factor, shift, choices } = task.data;
  const step1Ok = isFactorizationCorrect(value.step1, factor, shift);

  const updateStep1 = (nextStep) => {
    const nextOk = isFactorizationCorrect(nextStep, factor, shift);
    onChange?.({ ...value, step1: nextStep, reason: nextOk ? value.reason || "" : "" });
  };

  return (
    <div className="space-y-4" style={{ display: "grid", gap: 18, marginTop: 12, textAlign: "left" }}>
      <div>
        <div style={{ marginBottom: 6 }}>
          <strong>Krok 1.</strong> Wyłącz przed nawias wszystkie wspólne czynniki.
        </div>
        <EquationInput
          value={value.step1 || ""}
          onChange={updateStep1}
          disabled={disabled}
          placeholder="np. 7n(n+3)"
          showPreview
        />
      </div>
      <div>
        <div style={{ marginBottom: 6 }}>
          <strong>Krok 2.</strong> Wybierz uzasadnienie podzielności pozostałego iloczynu przez{" "}
          <InlineMath math="2" />.
        </div>
        <ReasonSelect
          choices={choices}
          value={value.reason || ""}
          disabled={disabled || !step1Ok}
          onChange={(reason) => onChange?.({ ...value, reason })}
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const fullFactorizationOk = isFactorizationCorrect(userAnswer?.step1, data.factor, data.shift);
  const divisorFactorOk = isDivisorFactorCorrect(userAnswer?.step1, data.factor, data.shift);
  const reasonOk = fullFactorizationOk && userAnswer?.reason === data.correctChoice;
  const score = Number(divisorFactorOk) + Number(reasonOk);
  let message = "";
  if (userAnswer?.step1 && !divisorFactorOk) {
    message = "Krok 1: najpierw wyłącz przed nawias wspólny czynnik " + data.factor + ".";
  } else if (divisorFactorOk && !fullFactorizationOk) {
    message = "Masz punkt za wykazanie podzielności przez " + data.factor + ". Aby odblokować krok 2, wyłącz przed nawias także n.";
  } else if (fullFactorizationOk && userAnswer?.reason && !reasonOk) {
    message = "Krok 2: liczby n oraz n + " + data.shift + " różnią się o liczbę nieparzystą.";
  }
  return {
    isCorrect: score === data.maxPoints,
    score,
    maxScore: data.maxPoints,
    parts: { divisorFactor: divisorFactorOk, fullFactorization: fullFactorizationOk, parityReason: reasonOk },
    ...(message ? { messageType: "warning", message } : {}),
  };
}

export function renderAnswer({ data }) {
  return (
    <div style={{ display: "grid", gap: 8, textAlign: "left" }}>
      <div><strong>Krok 1:</strong> <InlineMath math={data.factor + "n(n+" + data.shift + ")"} /></div>
      <div>
        <strong>Krok 2:</strong> Jedna z liczb <InlineMath math="n" /> oraz{" "}
        <InlineMath math={"n+" + data.shift} /> jest parzysta.
      </div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const expression = data.factor + "n^2+" + data.linearCoefficient + "n";
  const factored = data.factor + "n(n+" + data.shift + ")";
  return (
    <div className="space-y-3" style={{ display: "grid", gap: 14, textAlign: "left" }}>
      <div>
        <p><strong>Krok 1.</strong> Wyłączamy wspólne czynniki <InlineMath math={String(data.factor)} /> oraz <InlineMath math="n" />:</p>
        <BlockMath math={expression + "=" + factored} />
      </div>
      <div>
        <p>
          <strong>Krok 2.</strong> Liczby <InlineMath math="n" /> oraz <InlineMath math={"n+" + data.shift} /> różnią się
          o nieparzystą liczbę <InlineMath math={String(data.shift)} />, więc mają różną parzystość. Jedna z nich jest
          parzysta, zatem:
        </p>
        <BlockMath math={"n(n+" + data.shift + ")=2k,\\qquad k\\in\\mathbb{Z}"} />
      </div>
      <div>
        <p><strong>Krok 3.</strong> Podstawiamy tę postać do otrzymanego iloczynu:</p>
        <BlockMath math={factored + "=" + data.factor + "\\cdot2k=" + data.divisor + "k"} />
      </div>
      <p>
        <strong>Wniosek.</strong> Otrzymaliśmy wielokrotność <InlineMath math={String(data.divisor)} />, więc wyrażenie
        jest podzielne przez <InlineMath math={String(data.divisor)} /> dla każdej liczby całkowitej <InlineMath math="n" />.
      </p>
    </div>
  );
}

export function renderUserAnswer(userAnswer, task) {
  if (!userAnswer?.step1 && !userAnswer?.reason) return <i>(brak odpowiedzi)</i>;
  const selected = task?.data?.choices?.find((choice) => choice.value === userAnswer.reason);
  return (
    <div style={{ display: "grid", gap: 8, textAlign: "left" }}>
      <div><strong>Krok 1:</strong>{" "}{userAnswer?.step1 ? <InlineMath math={userAnswer.step1} /> : "—"}</div>
      <div><strong>Krok 2:</strong> {selected?.label || "—"}</div>
    </div>
  );
}